import { OrbitControls as DreiOrbitControls } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import type * as THREE from "three";
import type { CameraPreset } from "../../engines/types";
import { playCameraTween, shouldTweenCamera } from "./cameraTween";
import {
	clampOrbitTarget,
	MAX_ORBIT_DISTANCE_M,
	MAX_ORBIT_POLAR_ANGLE_RAD,
	MIN_ORBIT_DISTANCE_M,
} from "./viewerCameraLimits";

/** Minimal type for the controls ref — just what we need for camera presets + drag disable */
export type OrbitControlEvent = "change" | "start" | "end";

export interface OrbitControlsRef {
	target: THREE.Vector3;
	enabled: boolean;
	update: () => void;
	/**
	 * three's OrbitControls extends EventDispatcher and emits 'start',
	 * 'change', 'end'. Surfaced here so the idle-DoF sensor can listen
	 * without reaching for the underlying THREE class directly.
	 *
	 * Optional so test fixtures don't have to stub the EventDispatcher
	 * surface — sensors must feature-check before subscribing.
	 */
	addEventListener?: (type: OrbitControlEvent, listener: () => void) => void;
	removeEventListener?: (type: OrbitControlEvent, listener: () => void) => void;
	/**
	 * Also from EventDispatcher. The preset tween dispatches synthetic
	 * 'start'/'end' so interaction observers treat a programmatic move
	 * the same as a user orbit. Optional for the same fixture reason as
	 * the listeners above.
	 */
	dispatchEvent?: (event: { type: OrbitControlEvent }) => void;
}

interface SceneControlsProps {
	activePreset: CameraPreset;
	/** Exposed so DragController can disable orbit during drag */
	controlsRef: React.RefObject<OrbitControlsRef | null>;
}

/**
 * Drei OrbitControls with damping. Reads camera preset positions and
 * animates to them when the preset changes.
 *
 * The dolly/polar limits come from `viewerCameraLimits` — they are what
 * keeps the user from zooming out past the sky dome (which clips the
 * dome's far half and shows the page background through the canvas as a
 * giant dark ring) or orbiting under the ground plane. Pan is bounded
 * separately, in the 'change' handler below, because OrbitControls has
 * no prop for it. Every camera preset sits comfortably inside all of
 * them, so a preset tween can never fight a clamp.
 */
export function SceneControls({
	activePreset,
	controlsRef,
}: SceneControlsProps) {
	const camera = useThree((s) => s.camera);
	const invalidate = useThree((s) => s.invalidate);
	const gl = useThree((s) => s.gl);
	const size = useThree((s) => s.size);
	// `makeDefault` publishes the controls instance into the R3F store.
	// Reading it from there (rather than the forwarded ref) gives the pan
	// clamp below a *reactive* handle: it re-runs when the instance
	// actually exists, instead of racing the ref assignment on mount.
	const defaultControls = useThree(
		(s) => s.controls,
	) as OrbitControlsRef | null;
	// The parent recomputes the presets array whenever buildInput
	// changes (dimensions, openings, partition…). That gives us a new
	// preset *object* with the same `id` on every edit. Without this
	// guard the effect would snap the camera back to the preset every
	// time the user added a window or resized the sauna.
	const lastAppliedPresetId = useRef<string | null>(null);

	// Apply camera preset — only when the user actually switched presets,
	// not on every buildInput-triggered re-render. Preset switches ease
	// (tween) instead of teleporting; a direct copy is used under
	// automation / reduced-motion and when no rAF is available.
	useEffect(() => {
		const controls = defaultControls ?? controlsRef.current;
		if (!controls) return;
		const aspect = size.width / Math.max(1, size.height);
		const presetKey = `${activePreset.id}:${activePreset.framing ?? ""}:${aspect}`;
		if (lastAppliedPresetId.current === presetKey) return;
		lastAppliedPresetId.current = presetKey;

		// A portrait canvas has less horizontal field of view. Dolly along
		// the authored direction to keep its framing, rather than cropping it.
		const portraitScale = Math.max(1, 1 / Math.max(0.1, aspect));
		// Sida frames the nearest facade, which lies in front of the orbit
		// target. Leave room for that perspective depth and its edge trim.
		const framingScale = portraitScale > 1 && activePreset.id === "side"
			? portraitScale * 1.25 : portraitScale;
		const position = activePreset.target.clone().add(
			activePreset.position.clone().sub(activePreset.target)
				.multiplyScalar(framingScale),
		);

		const applyInstant = () => {
			camera.position.copy(position);
			controls.target.copy(activePreset.target);
			controls.update();
			invalidate();
		};

		if (!shouldTweenCamera()) {
			applyInstant();
			return;
		}

		const notifyStart = () => controls.dispatchEvent?.({ type: "start" });
		const notifyEnd = () => controls.dispatchEvent?.({ type: "end" });

		const cancelTween = playCameraTween(
			{ cameraPosition: camera.position, controlsTarget: controls.target },
			{ position, target: activePreset.target },
			{
				invalidate,
				updateControls: () => controls.update(),
				onStart: notifyStart,
				onEnd: notifyEnd,
			},
		);

		// User takeover: any pointerdown on the canvas aborts the tween
		// immediately so there's no fight over the camera. cancelTween
		// snaps to the final pose and fires onEnd (re-arming idle).
		const domElement = gl.domElement;
		const handleTakeover = () => cancelTween();
		domElement.addEventListener("pointerdown", handleTakeover);

		return () => {
			domElement.removeEventListener("pointerdown", handleTakeover);
			// Cleanup on unmount / preset change mid-flight: settle the
			// pose and stop pinning the demand frameloop.
			cancelTween();
		};
	}, [activePreset, camera, controlsRef, defaultControls, invalidate, gl, size.width, size.height]);

	// Bound the pan. OrbitControls clamps the dolly distance and the
	// polar angle for us, but the target itself is free: a right-drag /
	// two-finger pan walks it anywhere, and the camera rides along. Far
	// enough out and the camera leaves the sky dome again — the same dark
	// ring the distance clamp was added to kill — and panning the target
	// below grade sidesteps the polar clamp on the way down.
	//
	// Clamping in the 'change' handler keeps the gesture (and damping)
	// intact: OrbitControls re-derives its spherical from
	// `position − target` at the top of every update(), so pulling the
	// target back in simply converts further panning into a slight
	// dolly-in once the boundary is reached, instead of a hard stop.
	// `invalidate()` (only when something actually moved) drives that
	// next update() through the demand frameloop without recursing.
	useEffect(() => {
		const controls = defaultControls ?? controlsRef.current;
		if (!controls?.addEventListener || !controls.removeEventListener) return;

		const handleChange = () => {
			if (clampOrbitTarget(controls.target)) {
				invalidate();
			}
		};

		controls.addEventListener("change", handleChange);
		return () => controls.removeEventListener?.("change", handleChange);
	}, [defaultControls, controlsRef, invalidate]);

	return (
		<DreiOrbitControls
			ref={controlsRef as React.RefObject<never>}
			enableDamping
			makeDefault
			minDistance={MIN_ORBIT_DISTANCE_M}
			maxDistance={MAX_ORBIT_DISTANCE_M}
			maxPolarAngle={MAX_ORBIT_POLAR_ANGLE_RAD}
		/>
	);
}
